/* eslint-disable import/named */
import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useLayoutEffect
} from "react";
import PropTypes from "kf-proptypes/widgets";
import { t } from "@lingui/macro";

import { getS3FileKey, generateId } from "base/utils";

import { LazyLoadComponent } from "@widgets/others/lazyloader/index.jsx";
import {
  Icon,
  ICON_TYPES
} from "@widgets/common/components/primitives/icon/index.jsx";
import { Tooltip } from "@widgets/feedback/tooltip/index.jsx";
import { Menu, MenuItem } from "@widgets/inputs/dropdown/menu.jsx";
import { FormToolbar } from "./toolbar.jsx";
import {
  TOOLBAR_BUTTONS,
  FORMAT_TYPES,
  Toolbar,
  RichTextEditor
} from "../index.js";
import { POPOVER_POSITIONS } from "@widgets/feedback/popover/index.jsx";

import styles from "../richtext.css";

import ImageIcon from "./Image.svg";
import MoreIcon from "./More.svg";

const BUTTON_WIDGET_SIZE = 40;
const OPTIONS = {
  fileExtensions: ["JPG", "JPEG", "JPE", "PNG", "SVG", "GIF"],
  imageProps: {
    sizes: [[700, 700]],
    crop: false
  },
  maxCount: 1,
  tooltipPosition: POPOVER_POSITIONS.LEFT
};

FormRichTextContainer.propTypes = {
  showBorder: PropTypes.bool,
  customToolbarButton: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({
      type: PropTypes.string,
      button: PropTypes.element,
      label: PropTypes.string
    })
  ]),
  readOnly: PropTypes.bool,
  plugins: PropTypes.arrayOf(
    PropTypes.shape({
      formatType: PropTypes.string,
      component: PropTypes.node
    })
  ),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  className: PropTypes.string,
  onBlur: PropTypes.func,
  flowId: PropTypes.string,
  containerClassName: PropTypes.string,
  enableImage: PropTypes.bool,
  parentContainer: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  editorRef: PropTypes.shape({
    current: PropTypes.instanceOf(Element)
  }).isRequired,
  onToolbarClick: PropTypes.func
};

function FormRichTextContainer(props) {
  const {
    showBorder,
    customToolbarButton,
    className,
    flowId,
    containerClassName = "",
    enableImage = true,
    parentContainer,
    editorRef,
    onToolbarClick = () => {},
    ...restProps
  } = props;

  const randomUniqueId = useRef(generateId("Toolbar"));
  let TOOLBAR_OPTIONS = useRef({
    HEADING: {
      buttons: [TOOLBAR_BUTTONS.HEADER],
      className: `HEADING_${randomUniqueId.current}`
    },
    INLINE_FORMATTING: {
      width: BUTTON_WIDGET_SIZE * 7,
      buttons: [
        TOOLBAR_BUTTONS.BOLD,
        TOOLBAR_BUTTONS.ITALIC,
        TOOLBAR_BUTTONS.UNDERLINE,
        TOOLBAR_BUTTONS.STRIKE,
        TOOLBAR_BUTTONS.CODE,
        TOOLBAR_BUTTONS.BLOCKQUOTE
      ],
      className: `INLINE_FORMATTING_${randomUniqueId.current}`
    },
    COLOR: {
      buttons: [TOOLBAR_BUTTONS.COLOR],
      className: `COLOR_${randomUniqueId.current}`
    },
    CUSTOM_FIELD: {
      buttons: [],
      className: `CUSTOM_FIELD_${randomUniqueId.current}`
    },
    OTHERS: {
      width: BUTTON_WIDGET_SIZE * 8,
      buttons: [
        TOOLBAR_BUTTONS.OL_LIST,
        TOOLBAR_BUTTONS.UL_LIST,
        TOOLBAR_BUTTONS.ALIGN,
        TOOLBAR_BUTTONS.LINK,
        ...(enableImage
          ? [
              {
                type: FORMAT_TYPES.IMAGE,
                button: ({ label }) => (
                  <RichTextFilePicker
                    label={label}
                    flowId={flowId}
                    insertImage={insertImage}
                  />
                )
              }
            ]
          : []),
        TOOLBAR_BUTTONS.VIDEO,
        TOOLBAR_BUTTONS.ERASE
      ],
      className: `OTHERS_${randomUniqueId.current}`
    }
  });
  const currentEditorRef = useRef();
  const isIconMinimized = useRef(false);
  const [newToolbarButtons, setNewToolBarButtons] = useState(
    TOOLBAR_OPTIONS.current
  );
  const [showToolbarinPopover, setShowToolbarinPopover] = useState(false);

  useEffect(
    function updateCustomField() {
      if (customToolbarButton) {
        TOOLBAR_OPTIONS.current.CUSTOM_FIELD.buttons.push(customToolbarButton);
        setNewToolBarButtons({ ...TOOLBAR_OPTIONS.current });
      }
    },
    [customToolbarButton]
  );

  useEffect(
    function setEditorRef() {
      if (currentEditorRef.current) {
        if (editorRef) {
          editorRef.current = currentEditorRef.current;
        }
      }
    },
    [currentEditorRef, editorRef]
  );

  function insertImage(fileWrapperArray) {
    if (fileWrapperArray.length < 1) return;
    for (const fileWrapper of fileWrapperArray) {
      const { removeBlock } = currentEditorRef.current.insertBlock(
        FORMAT_TYPES.IMAGE,
        {
          src: "",
          loading: true
        }
      );
      if (fileWrapper) {
        fileWrapper.onResponseDone((err, file) => {
          if (err) return;
          let fileSrc = "";
          if (["svg", "gif"].includes(file.fileExtension)) {
            fileSrc = getS3FileKey(file.key, flowId);
          }
          if (file && file.photos) {
            fileSrc = getS3FileKey(file.photos[0].key, flowId);
          }
          if (fileSrc) {
            const blotIndex = removeBlock();
            if (blotIndex !== -1) {
              currentEditorRef.current.insertEmbedAtBlotIndex(
                blotIndex,
                FORMAT_TYPES.IMAGE,
                {
                  src: fileSrc
                }
              );
            }
          }
        });
      }
    }
  }

  useLayoutEffect(
    function calculateWidths() {
      function getWidth(selector) {
        return document.querySelector(selector)?.clientWidth || 0;
      }
      setTimeout(() => {
        const TOOLBAR_CONTAINER = getWidth(
          `.customtoolbar_${randomUniqueId.current}`
        );
        /** for lower widths range btw 200-400 */
        if (TOOLBAR_CONTAINER < 400 && TOOLBAR_CONTAINER > 200) {
          isIconMinimized.current = true;
          TOOLBAR_OPTIONS.current.INLINE_FORMATTING.visibleIconLength = 1;
          if (!customToolbarButton) {
            TOOLBAR_OPTIONS.current.OTHERS.visibleIconLength = 1;
          } else {
            TOOLBAR_OPTIONS.current.OTHERS.visibleIconLength = 0;
          }
          setNewToolBarButtons({ ...TOOLBAR_OPTIONS.current });
          return;
        } else if (TOOLBAR_CONTAINER < 200) {
          setNewToolBarButtons({ ...TOOLBAR_OPTIONS.current });
          setShowToolbarinPopover(true);
          return;
        }

        /** for other widths */
        const RESERVED_WIDTH =
          getWidth(`.HEADING_${randomUniqueId.current}`) +
          getWidth(`.COLOR_${randomUniqueId.current}`) +
          getWidth(`.CUSTOM_FIELD_${randomUniqueId.current}`);

        const NEEDED_WIDTH =
          TOOLBAR_OPTIONS.current.INLINE_FORMATTING.buttons.length *
            BUTTON_WIDGET_SIZE +
          TOOLBAR_OPTIONS.current.OTHERS.buttons.length * BUTTON_WIDGET_SIZE +
          3 * BUTTON_WIDGET_SIZE;

        const OTHER_BUTTONS = TOOLBAR_OPTIONS.current["OTHERS"].buttons.length;

        if (TOOLBAR_CONTAINER < NEEDED_WIDTH + RESERVED_WIDTH) {
          const AVAILABLE_WIDTH = TOOLBAR_CONTAINER - RESERVED_WIDTH;
          if (AVAILABLE_WIDTH < NEEDED_WIDTH) {
            let totalItemsFit = Math.trunc(
              AVAILABLE_WIDTH / BUTTON_WIDGET_SIZE
            );
            const isRoundingOff =
              Math.round(AVAILABLE_WIDTH / BUTTON_WIDGET_SIZE) - totalItemsFit;

            /** if itemstofit is equal to the inline formatting buttons reduce it by 2 */
            if (!isRoundingOff && totalItemsFit === OTHER_BUTTONS + 1) {
              totalItemsFit = totalItemsFit - 2;
            }

            if (totalItemsFit < OTHER_BUTTONS) {
              TOOLBAR_OPTIONS.current["OTHERS"].visibleIconLength =
                totalItemsFit - 3 > 0 ? totalItemsFit - 3 : 0;
              TOOLBAR_OPTIONS.current[
                "INLINE_FORMATTING"
              ].visibleIconLength = 1;
            } else {
              const OTHERS_LENGTH = totalItemsFit - OTHER_BUTTONS - 1;
              if (OTHERS_LENGTH > 0) {
                TOOLBAR_OPTIONS.current["INLINE_FORMATTING"].visibleIconLength =
                  OTHERS_LENGTH - 1;
              } else {
                TOOLBAR_OPTIONS.current[
                  "INLINE_FORMATTING"
                ].visibleIconLength = 1;
                TOOLBAR_OPTIONS.current["OTHERS"].visibleIconLength =
                  totalItemsFit - 4;
              }
            }
          }
        }
        setNewToolBarButtons({ ...TOOLBAR_OPTIONS.current });
      }, 100);
    },
    [customToolbarButton]
  );

  return (
    <div
      className={`${containerClassName} ${styles.richtextcontainer} ${
        showBorder ? styles.richtextborder : ""
      }`}
    >
      <div className={`customtoolbar_${randomUniqueId.current}`}>
        {showToolbarinPopover ? (
          <Menu
            getContainer={() => parentContainer || document.body}
            targetRenderer={() => {
              return (
                <div className={styles.moreMenu}>
                  <Icon
                    fill="var(--kf-color-gray-700)"
                    clickable={true}
                    type={ICON_TYPES.TYPE4}
                  >
                    <MoreIcon />
                  </Icon>
                </div>
              );
            }}
          >
            <Toolbar
              toolbarButtons={newToolbarButtons}
              toolbarRef={currentEditorRef}
              iconSize={16}
              className={`${styles.toolbar} ${
                showBorder ? styles.toolbarborder : ""
              }`}
              toolbarRenderer={(props) => {
                return (
                  <FormToolbar
                    {...props}
                    flowId={flowId}
                    isIconMinimized={isIconMinimized.current}
                    toolbarButtons={newToolbarButtons}
                  />
                );
              }}
              isFormToolbar={true}
              isIconMinimized={isIconMinimized.current}
              onClick={onToolbarClick}
            />
          </Menu>
        ) : (
          <Toolbar
            toolbarButtons={newToolbarButtons}
            toolbarRef={currentEditorRef}
            iconSize={16}
            className={`${styles.toolbar} ${
              showBorder ? styles.toolbarborder : ""
            }`}
            toolbarRenderer={(props) => {
              return (
                <FormToolbar
                  {...props}
                  flowId={flowId}
                  isIconMinimized={isIconMinimized.current}
                  toolbarButtons={newToolbarButtons}
                  parentContainer={parentContainer}
                />
              );
            }}
            isFormToolbar={true}
            isIconMinimized={isIconMinimized.current}
            onClick={onToolbarClick}
          />
        )}
      </div>
      <RichTextEditor
        ref={currentEditorRef}
        {...restProps}
        className={`${className} ${styles.richtext} ${
          showBorder ? styles.richtextbottomborder : ""
        }`}
      />
    </div>
  );
}

export const FormRichTextLegacy = forwardRef((props, ref) => {
  return <FormRichTextContainer editorRef={ref} {...props} />;
});

function RichTextFilePicker({ label, flowId, insertImage }) {
  const accountId = window.accountDetails._id;

  const [showFilePicker, setShowFilePicker] = useState(false);

  if (label) {
    return (
      <Menu>
        <MenuItem>
          <LazyLoadComponent
            component={() => import("kfplatform/filePicker")}
            componentName="FilePickerV2"
            open={showFilePicker}
            options={OPTIONS}
            prefix={flowId}
            onFileChoosen={insertImage}
            onPickerClose={() => setShowFilePicker(false)}
            baseURL={`/upload/2/${accountId}/`}
            targetClassname={styles.filePickerTarget}
          >
            <div
              onClick={() => setShowFilePicker((isOpen) => !isOpen)}
              className={styles.iconwithlabel}
              onMouseEnter={() => {
                setShowFilePicker(true);
              }}
            >
              <ImageIcon
                width={16}
                height={16}
                fill={"var(--kf-color-gray-700)"}
              />
              <span className={"base"}>{t`Image`}</span>
            </div>
          </LazyLoadComponent>
        </MenuItem>
      </Menu>
    );
  } else {
    return (
      <LazyLoadComponent
        component={() => import("kfplatform/filePicker")}
        componentName="FilePickerV2"
        open={showFilePicker}
        options={OPTIONS}
        prefix={flowId}
        onFileChoosen={insertImage}
        onPickerClose={() => setShowFilePicker(false)}
        baseURL={`/upload/2/${accountId}/`}
      >
        <Tooltip content={t`Add an image`}>
          <Icon
            key={"image"}
            onClick={() => setShowFilePicker(true)}
            type={ICON_TYPES.TYPE4}
          >
            <ImageIcon
              width={16}
              height={16}
              fill={"var(--kf-color-gray-700)"}
            />
          </Icon>
        </Tooltip>
      </LazyLoadComponent>
    );
  }
}

RichTextFilePicker.propTypes = {
  label: PropTypes.string,
  flowId: PropTypes.string,
  insertImage: PropTypes.func
};
